import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import UserProfile from './components/UserProfile';
import PaymentPage from './components/PaymentPage';
import { HAS_PAYMENTS, IS_EUROPE, BRAND, SITE_URL, ROUTER_BASENAME } from './config/region';
import { needsConsentDecision, COOKIE_SETTINGS_EVENT } from './utils/consent';
import Building from './components/Building';
import BuildingNovelDetail from './components/BuildingNovelDetail';
import InteractiveSceneDetail from './components/InteractiveSceneDetail';
import StaticSceneDetail from './components/StaticSceneDetail';
import OpenWorldSceneDetail from './components/OpenWorldSceneDetail';
import ProductionNovelPlay from './components/ProductionNovelPlay';
import ProductionNovelDetail from './components/ProductionNovelDetail';
import GamePlay from './components/GamePlay';
import SearchPage from './components/SearchPage';
import PlayPage from './components/PlayPage';
import LibraryPage from './components/LibraryPage';
import LibraryNovelDetail from './components/LibraryNovelDetail';
import SiteFooter from './components/SiteFooter';
import SiteHeader from './components/SiteHeader';
import { reportPendingPurchases } from './utils/ecommerce';
import { trackAdsPageView } from './utils/googleAds';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import CookiesPage from './components/CookiesPage';
import OfertaPage from './components/OfertaPage';
import OfferPage from './components/OfferPage';
import AgencyAgreementPage from './components/AgencyAgreementPage';
import CopyrightPage from './components/CopyrightPage';
import LegalInfoPage from './components/LegalInfoPage';
import PrivacyRequestPage from './components/PrivacyRequestPage';
import RefundPage from './components/RefundPage';
import CookieBanner from './components/CookieBanner';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import NewsPage from './components/NewsPage';
import NewsPostPage from './components/NewsPostPage';
import AuthorsPage from './components/AuthorsPage';
import ConfirmEmailPage from './components/ConfirmEmailPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import AdminReportsPage from './components/AdminReportsPage';
import { t } from './i18n';
import './App.css';

const MAINTENANCE_MODE = false;

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPlayPage = location.pathname.includes('/play/');
  /* russia stores a single accepted/not flag; europe needs a per-category record, and a Global
     Privacy Control signal counts as an answer already given — see utils/consent.js. */
  const [showConsent, setShowConsent] = useState(() => (
    IS_EUROPE ? needsConsentDecision() : localStorage.getItem('cookieConsent') !== 'true'
  ));

  /* The footer can reopen the preference centre. A window event rather than a prop or a context:
     the banner's visibility lives here, the link lives several levels down, and this app has no
     store to put shared state in. */
  /* Reopened from the footer, the visitor asked for the settings specifically — so go straight to
     the category list rather than making them answer the first-visit prompt again. */
  const [consentInSettings, setConsentInSettings] = useState(false);

  useEffect(() => {
    const reopen = () => { setConsentInSettings(true); setShowConsent(true); };
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  /* Ecommerce `purchase` (Metrica in russia, GA4 in europe) for payments the backend has confirmed
     since this browser last asked. App-level rather than on the top-up page: the player comes back
     from the payment form to whichever page the provider's return URL names, and the purchase has
     to be reported wherever they land. In europe it waits for analytics consent and returns the
     unsubscribe for that wait. */
  useEffect(() => reportPendingPurchases(), []);

  /* Google Ads only sees the page it loaded on; route changes are reported here. See utils/googleAds.js. */
  useEffect(() => {
    trackAdsPageView();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname.startsWith('/russian')) {
      const newPath = location.pathname.replace(/^\/russian/, '') || '/';
      navigate(newPath + location.search + location.hash, { replace: true });
    }
  }, [location, navigate]);

  return (
    <>
      {!isPlayPage && <SiteHeader />}
      <Routes>
        <Route path="/"                                                         element={<SearchPage />} />
        <Route path="/user/me"                                                  element={<UserProfile />} />
        {HAS_PAYMENTS && <Route path="/payment"                                  element={<PaymentPage />} />}
        <Route path="/building"                                                 element={<Building />} />
        <Route path="/building/:novelId"                                        element={<BuildingNovelDetail />} />
        <Route path="/building/:novelId/interactive_scene/:sceneId"            element={<InteractiveSceneDetail />} />
        <Route path="/building/:novelId/static_scene/:sceneId"                 element={<StaticSceneDetail />} />
        <Route path="/building/:novelId/open_world_scene/:sceneId"            element={<OpenWorldSceneDetail />} />
        <Route path="/production/:playLink"                                     element={<ProductionNovelPlay />} />
        <Route path="/production_change/:novelId"                               element={<ProductionNovelDetail />} />
        <Route path="/production_change/:novelId/interactive_scene/:sceneId"   element={<InteractiveSceneDetail />} />
        <Route path="/production_change/:novelId/static_scene/:sceneId"        element={<StaticSceneDetail />} />
        <Route path="/production_change/:novelId/open_world_scene/:sceneId"  element={<OpenWorldSceneDetail />} />
        <Route path="/game/:novelId/state/:gameStateId"                        element={<GamePlay />} />
        <Route path="/play/:playLink"                                           element={<PlayPage />} />
        <Route path="/search"                                                   element={<SearchPage />} />
        <Route path="/library"                                                  element={<LibraryPage />} />
        <Route path="/library/:playLink"                                        element={<LibraryNovelDetail />} />
        <Route path="/terms"                                                    element={<TermsPage />} />
        <Route path="/privacy"                                                  element={<PrivacyPage />} />
        <Route path="/cookies"                                                  element={<CookiesPage />} />
        <Route path="/oferta"                                                   element={<OfertaPage />} />
        <Route path="/author-offer"                                             element={<OfferPage />} />
        <Route path="/author-agency-agreement"                                  element={<AgencyAgreementPage />} />
        <Route path="/copyright"                                                element={<CopyrightPage />} />
        {IS_EUROPE && <Route path="/legal"                                        element={<LegalInfoPage />} />}
        {IS_EUROPE && <Route path="/privacy-request"                              element={<PrivacyRequestPage />} />}
        <Route path="/refund"                                                   element={<RefundPage />} />
        <Route path="/blog"                                                     element={<BlogPage />} />
        <Route path="/blog/:slug"                                                element={<BlogPostPage />} />
        <Route path="/news"                                                      element={<NewsPage />} />
        <Route path="/news/:slug"                                                element={<NewsPostPage />} />
        {/* Russia only: the page recruits authors through a Russian-language intake Plotex does
            not run, and its Telegram/Yandex contacts do not exist for europe. */}
        {!IS_EUROPE && <Route path="/authors"                                       element={<AuthorsPage />} />}
        <Route path="/confirm-email"                                             element={<ConfirmEmailPage />} />
        <Route path="/reset-password"                                            element={<ResetPasswordPage />} />
        <Route path="/admin/reports"                                             element={<AdminReportsPage />} />
      </Routes>

      {!isPlayPage && <SiteFooter />}
      {!isPlayPage && showConsent && (
        <CookieBanner
          openInSettings={consentInSettings}
          onAccept={() => { setShowConsent(false); setConsentInSettings(false); }}
        />
      )}
    </>
  );
};

function App() {
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
    }
  }, []);

  if (MAINTENANCE_MODE) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        background: '#0a0a0f',
        fontFamily: "'Alegreya Sans', sans-serif",
        color: 'rgba(255,255,255,0.75)',
        fontSize: '1.2rem',
        textAlign: 'center',
        padding: '2rem',
      }}>
        <span>{t('Проводятся технические работы. Пожалуйста вернитесь позже.')}</span>
        <button
          onClick={() => { window.location.href = window.location.origin + '?t=' + Date.now(); }}
          style={{
            background: 'rgba(123,47,255,0.15)',
            border: '1px solid rgba(123,47,255,0.5)',
            borderRadius: '8px',
            color: 'rgba(255,255,255,0.75)',
            fontFamily: "'Alegreya Sans', sans-serif",
            fontSize: '1rem',
            padding: '0.6rem 1.4rem',
            cursor: 'pointer',
          }}
        >
          {t('Обновить')}
        </button>
      </div>
    );
  }

  return (
    <HelmetProvider>
      {IS_EUROPE ? (
        <Helmet>
        <title>{BRAND} — AI-powered interactive novels</title>
        <meta name="description" content={`${BRAND} — a platform for interactive visual novels with AI. Play and create stories where the characters answer you in your own language.`} />
        <meta name="keywords" content="interactive novels, visual novels, AI novels, interactive stories, AI storytelling" />
        <meta property="og:site_name" content={BRAND} />
        <meta property="og:title" content={`${BRAND} — AI-powered interactive novels`} />
        <meta property="og:description" content="A platform for interactive visual novels with AI. Play and create stories where the characters answer you in your own language." />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SITE_URL}/logo512.png`} />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content={`${BRAND} logo`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${BRAND} — AI-powered interactive novels`} />
        <meta name="twitter:description" content="Play and create interactive visual novels with artificial intelligence." />
        <meta name="twitter:image" content={`${SITE_URL}/logo512.png`} />
      </Helmet>
      ) : (
      <Helmet>
        <title>AI Novel — Интерактивные новеллы с искусственным интеллектом</title>
        <meta name="description" content="AI Novel — платформа интерактивных визуальных новелл с ИИ. Создавай и играй в новеллы, где персонажи отвечают с помощью искусственного интеллекта. Стань соавтором истории." />
        <meta name="keywords" content="интерактивные новеллы, визуальные новеллы, ИИ новеллы, AI novel, искусственный интеллект, интерактивные истории, создать новеллу, играть в новеллу, ainovel" />
        <meta property="og:site_name" content="AI Novel" />
        <meta property="og:title" content="AI Novel — Интерактивные новеллы с искусственным интеллектом" />
        <meta property="og:description" content="Платформа интерактивных визуальных новелл с ИИ. Создавай и играй в новеллы, где персонажи отвечают с помощью искусственного интеллекта." />
        <meta property="og:url" content="https://ainovel.ru" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ru_RU" />
        <meta property="og:image" content="https://ainovel.ru/logo512.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content="AI Novel — логотип платформы интерактивных новелл" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="AI Novel — Интерактивные новеллы с ИИ" />
        <meta name="twitter:description" content="Создавай и играй в интерактивные визуальные новеллы с искусственным интеллектом." />
        <meta name="twitter:image" content="https://ainovel.ru/logo512.png" />
      </Helmet>
      )}
      <Router basename={ROUTER_BASENAME}>
        <div className="App">
          <AppContent />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
